import { computed } from 'vue'
import { useTabSessionStore } from '~/stores/tabSession'

/**
 * "Market Value as of {date}" date used in the global footer
 * (components/footer/MainInfo.vue).
 *
 * HOUSEHOLD-SCOPED + import-driven. Backed by /api/ui/market-asof which returns:
 *   - the per-household OVERRIDE date when an admin manually refreshed that one
 *     household (cleared every nightly), else
 *   - the GLOBAL date = the most recent SUCCESSFUL full/nightly import's
 *     snapshot date.
 *
 * Crucially this is tied to actual IMPORT completion, NOT the query-cache
 * generation — busting the cache (which can happen for any reason) never moves
 * the footer date. (The household-VALUE staleness check is the one that's
 * generation-based; this date is not.)
 *
 * SSR correctness — same notes as before. On SSR the tab store isn't hydrated
 * (sessionStorage is client-only), so householdId is 0 → the global date; on
 * the client it re-resolves with the selected household (and any override). The
 * transport is a serialized 'YYYY-MM-DD' string so SSR/client stay identical.
 */
type AsOfResp = { asOfDate: string | null, isOverride: boolean, globalDate: string | null }

export function useMarketValueAsOfDate() {
  const tab = useTabSessionStore()

  const { data } = useAsyncData<AsOfResp>(
    'ui:market-asof',
    async () => {
      try {
        // Forward the session cookie during SSR — see useDisplayAccess.
        const headers = import.meta.server ? useRequestHeaders(['cookie']) : undefined
        return await $fetch<AsOfResp>('/api/ui/market-asof', {
          query: { householdId: tab.householdId ?? 0 },
          credentials: 'include',
          headers
        })
      } catch {
        // Decorative info — fall back to today via the computed below.
        return { asOfDate: null, isOverride: false, globalDate: null }
      }
    },
    // Re-resolve when the active household changes (its override may differ).
    { watch: [() => tab.householdId] }
  )

  const asOfDate = computed<Date>(() => {
    const s = data.value?.asOfDate
    // Parse YYYY-MM-DD as a LOCAL date — naive `new Date('YYYY-MM-DD')` is UTC
    // midnight which can render the previous day in the user's timezone.
    if (s) {
      const [year, month, day] = s.split('-').map(Number)
      if (year && month && day) return new Date(year, month - 1, day)
    }
    // No import yet — fall back to today (same rationale as the old version).
    return new Date()
  })

  return asOfDate
}
